import { FC, useState, useEffect } from 'react';
import { ApiService, ApiKeyResponse } from '../services/api';
import toast from 'react-hot-toast';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, Calendar, CheckCircle2, XCircle } from 'lucide-react';

const ApiKeys: FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<{ key: string; id: string } | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  // Helper to store API key in localStorage
  const storeApiKey = (keyId: string, fullKey: string) => {
    const storageKey = `api_key_${keyId}`;
    const data = {
      key: fullKey,
      timestamp: Date.now(),
    };
    localStorage.setItem(storageKey, JSON.stringify(data));
  };

  // Helper to retrieve API key from localStorage
  const getStoredApiKey = (keyId: string): string | null => {
    const storageKey = `api_key_${keyId}`;
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    
    try {
      const data = JSON.parse(stored);
      // Keep keys for 7 days
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > sevenDays) {
        localStorage.removeItem(storageKey);
        return null;
      }
      return data.key;
    } catch {
      localStorage.removeItem(storageKey);
      return null;
    }
  };

  // Helper to remove stored API key
  const removeStoredApiKey = (keyId: string) => {
    const storageKey = `api_key_${keyId}`;
    localStorage.removeItem(storageKey);
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const keys = await ApiService.getApiKeys();
      setApiKeys(keys);
    } catch (error: any) {
      console.error('Error fetching API keys:', error);
      toast.error(error?.response?.data?.message || 'Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    try {
      const result = await ApiService.createApiKey(newKeyName || undefined);
      // Store the full key in localStorage for later access
      storeApiKey(result.id, result.key);
      setNewlyCreatedKey({ key: result.key, id: result.id });
      setNewKeyName('');
      setShowCreateModal(false);
      toast.success('API key created successfully! You can copy it now or later from the list.');
      await fetchApiKeys();
    } catch (error: any) {
      console.error('Error creating API key:', error);
      toast.error(error?.response?.data?.message || 'Failed to create API key');
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await ApiService.deleteApiKey(id);
      removeStoredApiKey(id); // Remove from localStorage if stored
      toast.success('API key deleted successfully');
      await fetchApiKeys();
    } catch (error: any) {
      console.error('Error deleting API key:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete API key');
    }
  };

  const handleRevokeApiKey = async (id: string) => {
    if (!confirm('Are you sure you want to revoke this API key? It will no longer be usable.')) {
      return;
    }

    try {
      await ApiService.revokeApiKey(id);
      toast.success('API key revoked successfully');
      await fetchApiKeys();
    } catch (error: any) {
      console.error('Error revoking API key:', error);
      toast.error(error?.response?.data?.message || 'Failed to revoke API key');
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };

  const toggleKeyVisibility = (keyId: string) => {
    const newVisible = new Set(visibleKeys);
    if (newVisible.has(keyId)) {
      newVisible.delete(keyId);
    } else {
      newVisible.add(keyId);
    }
    setVisibleKeys(newVisible);
  };

  const getFullKeyForDisplay = (keyId: string): string | null => {
    return getStoredApiKey(keyId);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading API keys...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">API Keys</h1>
          <p className="mt-1 sm:mt-2 text-sm text-gray-400">
            Manage your API keys for third-party integrations
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create API Key
        </button>
      </div>

      {/* New Key Display Modal */}
      {newlyCreatedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-[#182233] rounded-lg p-6 max-w-2xl w-full mx-4 border border-gray-600">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">API Key Created</h2>
              <button
                onClick={() => setNewlyCreatedKey(null)}
                className="text-gray-400 hover:text-white"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="mb-4 p-4 bg-yellow-900/20 border border-yellow-600 rounded-lg">
              <p className="text-yellow-400 text-sm font-medium mb-2">
                ⚠️ Important: Save your API key securely
              </p>
              <p className="text-gray-300 text-sm">
                This key is saved locally for 7 days, but you should store it in a secure location (password manager, environment variables, etc.) as it won't be shown again after that period.
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Your API Key
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={newlyCreatedKey.key}
                  className="flex-1 px-4 py-2 bg-[#0E131A] border border-gray-600 rounded-lg text-white font-mono text-sm"
                />
                <button
                  onClick={() => handleCopyKey(newlyCreatedKey.key)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  title="Copy API key"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="mb-4 p-3 bg-blue-900/20 border border-blue-600 rounded-lg">
              <p className="text-blue-300 text-sm">
                💡 <strong>Tip:</strong> This key is saved locally for 7 days. You can copy it later from the API keys list below.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setNewlyCreatedKey(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-[#182233] rounded-lg p-6 max-w-md w-full mx-4 border border-gray-600">
            <h2 className="text-xl font-bold text-white mb-4">Create New API Key</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Name (optional)
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production API Key"
                className="w-full px-4 py-2 bg-[#0E131A] border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewKeyName('');
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateApiKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div className="text-center py-12 bg-[#182233] rounded-lg border border-gray-600">
          <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No API keys</h3>
          <p className="text-gray-400 mb-4">Create your first API key to get started</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create API Key
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((key) => (
            <div
              key={key.id}
              className="bg-[#182233] rounded-lg border border-gray-600 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Key className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-medium text-white">
                      {key.name || 'Unnamed API Key'}
                    </h3>
                    {key.isActive ? (
                      <span className="flex items-center gap-1 px-2 py-1 bg-green-900/30 text-green-400 rounded text-xs">
                        <CheckCircle2 className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-2 py-1 bg-red-900/30 text-red-400 rounded text-xs">
                        <XCircle className="w-3 h-3" />
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Created: {formatDate(key.createdAt)}
                    </div>
                    {key.lastUsedAt && (
                      <div className="flex items-center gap-1">
                        Last used: {formatDate(key.lastUsedAt)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const storedKey = getFullKeyForDisplay(key.id);
                      const isVisible = visibleKeys.has(key.id);
                      const showFullKey = storedKey && isVisible;

                      return (
                        <>
                          <code className="px-3 py-2 bg-[#0E131A] border border-gray-600 rounded text-sm text-gray-300 font-mono">
                            {showFullKey ? storedKey : `${key.keyPrefix}...`}
                          </code>
                          {storedKey ? (
                            <>
                              <button
                                onClick={() => toggleKeyVisibility(key.id)}
                                className="p-2 text-gray-400 hover:text-white transition-colors"
                                title={isVisible ? "Hide full key" : "Show full key"}
                              >
                                {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                              {isVisible && (
                                <button
                                  onClick={() => handleCopyKey(storedKey)}
                                  className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
                                  title="Copy full key"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              onClick={() => handleCopyKey(key.keyPrefix + '...')}
                              className="p-2 text-gray-400 hover:text-white transition-colors"
                              title="Copy prefix"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {key.isActive ? (
                    <button
                      onClick={() => handleRevokeApiKey(key.id)}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm"
                    >
                      Revoke
                    </button>
                  ) : null}
                  <button
                    onClick={() => handleDeleteApiKey(key.id)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Usage Instructions */}
      <div className="mt-8 bg-[#182233] rounded-lg border border-gray-600 p-6">
        <h2 className="text-lg font-medium text-white mb-4">How to Use API Keys</h2>
        <div className="space-y-4 text-sm text-gray-300">
          <div>
            <h3 className="font-medium text-white mb-2">1. Include in Request Headers</h3>
            <code className="block px-4 py-2 bg-[#0E131A] border border-gray-600 rounded text-xs mt-2">
              Authorization: ApiKey sk_live_your_api_key_here
            </code>
            <p className="text-gray-400 mt-2">or</p>
            <code className="block px-4 py-2 bg-[#0E131A] border border-gray-600 rounded text-xs mt-2">
              X-API-Key: sk_live_your_api_key_here
            </code>
          </div>
          <div>
            <h3 className="font-medium text-white mb-2">2. Example cURL Request</h3>
            <code className="block px-4 py-2 bg-[#0E131A] border border-gray-600 rounded text-xs mt-2 whitespace-pre-wrap">
{`curl -X GET "https://api.example.com/api/v1/settlements" \\
  -H "Authorization: ApiKey sk_live_your_api_key_here"`}
            </code>
          </div>
          <div>
            <h3 className="font-medium text-white mb-2">3. Security Best Practices</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-400 ml-2">
              <li>Never commit API keys to version control</li>
              <li>Store keys securely (environment variables, secret managers)</li>
              <li>Rotate keys regularly</li>
              <li>Revoke unused or compromised keys immediately</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeys;

